import { AI_PAGE_GROUP_ROLES, type AiPageGroupRole } from "../registry/schema.js";
import { bandRank, type GuideBand } from "./bands.js";
import {
  GUIDE_CARD_LIBRARY,
  GUIDE_SILENT_COMBOS,
  GUIDE_SILENT_TRIGGERS,
  MAX_SILENT_COMBOS,
  MAX_SILENT_TRIGGERS,
  type GuideCard,
  type GuideSilentCombo,
  type GuideSilentDecision,
  type GuideSilentTrigger,
} from "./cards.js";
import { guideCardOutcomes, type GuideCardOutcome, type GuideClaim } from "./card-variants.js";
import type { GuidePredicate } from "./predicate.js";
import type { GuideSignalKey } from "./signals.js";
import { GUIDE_TRIGGERS, type GuideTrigger } from "./triggers.js";

/**
 * 🔴 US-AIV2-0065-S02 (@UC0217 @UC0218) — MA TRẬN tình huống × vai. MỘT hàm dựng, hai người đọc:
 *  · `scripts/gen-guide-matrix.mjs` in bảng `docs/generated/guide-matrix.md` cho PO duyệt từng ô;
 *  · gate **G14h v2** (`assert-ai-greeting-matrix-fresh.sh`) đỏ khi `problems` không rỗng hoặc bảng mục.
 *
 * Thay cho nhánh "trỏ Sổ" cũ của G14h (đỏ khi có trang `live` vì *"gate v2 chưa viết"*): nay G14h đỏ vì ĐÚNG lý do
 * của nó — một tình huống có tên mà câm, hoặc một ô trống không ai khai lý do.
 *
 * Luật của một ô (trigger × vai), theo thứ tự:
 *  1. có ≥1 thẻ khai trigger đó và mở cho vai đó ⇒ **CÓ THẺ**;
 *  2. không thẻ, nhưng ô được khai ở `GUIDE_SILENT_COMBOS` ⇒ **IM (ô)**, kèm lý do;
 *  3. không thẻ, nhưng CẢ trigger được khai ở `GUIDE_SILENT_TRIGGERS` ⇒ **IM (cả hàng)**, kèm chủ + lý do;
 *  4. còn lại ⇒ **TRỐNG** — và đó là một vấn đề có tên (`CELL_WITHOUT_REASON`), không phải một ô trắng trong bảng.
 *
 * 🔴 "Mọi trigger có ≥1 thẻ" (@UC0218) đọc CÙNG luật khai im lặng: trigger 0 thẻ chỉ hợp lệ khi có mặt ở
 * `GUIDE_SILENT_TRIGGERS` với chủ + lý do (QĐ PO 23/09 · 24/09 cho 6/7 dòng hôm nay). Đọc @UC0218 theo nghĩa đen
 * ("không trigger nào 0 thẻ") thì gate đỏ VĨNH VIỄN vì những QĐ "không làm" có chủ đích — và một gate đỏ vĩnh viễn là
 * một gate người ta học cách bỏ qua.
 *
 * Hàm THUẦN, đầu vào TIÊM được (mặc định = dữ liệu thật) ⇒ test dựng đúng một khoảng trống rồi đòi hàm gọi tên nó.
 */

/** Vai của ma trận — SUY từ Sổ (`AI_PAGE_GROUP_ROLES`) + cột `null` "chưa thuộc nhóm nào". Không danh sách tay. */
export const GUIDE_MATRIX_ROLES: readonly (AiPageGroupRole | null)[] = Object.freeze([...AI_PAGE_GROUP_ROLES, null]);

/**
 * Lý do ngắn hơn mức này không phải lý do. Khớp `coverage.invariants.spec.ts` PK-1 (`reason.length > 15`) — một ngưỡng,
 * hai chỗ đọc; `matrix.test.ts` khoá biên.
 */
export const GUIDE_SILENT_REASON_MIN = 16;

export interface GuideMatrixInput {
  readonly triggers: readonly GuideTrigger[];
  readonly library: readonly GuideCard[];
  readonly silentTriggers: readonly GuideSilentTrigger[];
  readonly silentCombos: readonly GuideSilentCombo[];
  /**
   * Trần của hai danh sách khai (`MAX_SILENT_TRIGGERS`/`MAX_SILENT_COMBOS`). Có ⇒ số dòng khai phải BẰNG trần (ratchet khít,
   * cùng kỷ luật `toBe` của PK-1 — nay G14h đo được, không chỉ vitest). Vắng ⇒ không so (fixture test dựng tay).
   */
  readonly caps?: { readonly triggers: number; readonly combos: number };
}

export type GuideMatrixCell =
  | { readonly kind: "cards"; readonly trigger: GuideTrigger; readonly role: AiPageGroupRole | null; readonly cards: readonly GuideCard[] }
  | {
      readonly kind: "silent";
      readonly trigger: GuideTrigger;
      readonly role: AiPageGroupRole | null;
      readonly scope: "trigger" | "cell";
      readonly reason: string;
      readonly owner?: string;
    }
  | { readonly kind: "empty"; readonly trigger: GuideTrigger; readonly role: AiPageGroupRole | null };

export interface GuideMatrixRow {
  readonly trigger: GuideTrigger;
  /** Mọi thẻ khai trigger này, bất kể vai — đếm "trigger có ≥1 thẻ". */
  readonly cards: readonly GuideCard[];
  readonly cells: readonly GuideMatrixCell[];
}

export type GuideMatrixProblemCode =
  | "TRIGGER_WITHOUT_CARD"
  | "CELL_WITHOUT_REASON"
  | "SILENT_WITHOUT_REASON"
  | "SILENT_WITHOUT_OWNER"
  | "SILENT_OVER_CAP"
  | "SILENT_CAP_STALE"
  | "SILENT_TRIGGER_HAS_CARD"
  | "SILENT_CELL_HAS_CARD"
  | "SILENT_UNKNOWN_TRIGGER";

export interface GuideMatrixProblem {
  readonly code: GuideMatrixProblemCode;
  readonly trigger: GuideTrigger;
  /** Chỉ có ở vấn đề cấp ô; `null` = cột "chưa thuộc nhóm". */
  readonly role?: AiPageGroupRole | null;
  readonly detail: string;
}

export interface GuideMatrix {
  readonly roles: readonly (AiPageGroupRole | null)[];
  readonly rows: readonly GuideMatrixRow[];
  readonly problems: readonly GuideMatrixProblem[];
}

const DEFAULT_INPUT: GuideMatrixInput = {
  triggers: GUIDE_TRIGGERS,
  library: GUIDE_CARD_LIBRARY,
  silentTriggers: GUIDE_SILENT_TRIGGERS,
  silentCombos: GUIDE_SILENT_COMBOS,
  caps: { triggers: MAX_SILENT_TRIGGERS, combos: MAX_SILENT_COMBOS },
};

const roleKey = (role: AiPageGroupRole | null): string => role ?? "none";

/** Thẻ phủ ô (trigger × vai), đúng thứ tự PO đọc: bậc cao trước, cùng bậc thì `priority` cao trước. */
export function guideCardsFor(
  trigger: GuideTrigger,
  role: AiPageGroupRole | null,
  library: readonly GuideCard[] = GUIDE_CARD_LIBRARY,
): GuideCard[] {
  return library
    .filter((c) => c.triggers.includes(trigger) && (c.roles.includes("*") || (role !== null && c.roles.includes(role))))
    .sort(byBandThenPriority);
}

/** Bậc cao trước, cùng bậc thì `priority` cao trước — cùng thứ tự `journey()` chọn (QĐ PO 18/08: `band` LỌC · `priority` SẮP). */
function byBandThenPriority(a: GuideCard, b: GuideCard): number {
  return bandRank(a.band) - bandRank(b.band) || b.priority - a.priority;
}

/**
 * Lý do phải là CÂU, không phải độ dài (review #0065 MEDIUM-1, đột biến M3: "................" từng qua). Hai vế: đủ dài, VÀ
 * ≥ `GUIDE_SILENT_REASON_MIN_WORDS` từ có chữ cái (`\p{L}` — chữ tiếng Việt có dấu tính là chữ). "aaaa…" một từ ⇒ trượt.
 */
export const GUIDE_SILENT_REASON_MIN_WORDS = 3;
const hasReason = (reason: string): boolean =>
  typeof reason === "string" &&
  reason.trim().length >= GUIDE_SILENT_REASON_MIN &&
  reason.split(/\s+/u).filter((w) => /\p{L}/u.test(w)).length >= GUIDE_SILENT_REASON_MIN_WORDS;

/** Mã task (`US-AIV2-0065`, `US-AIV2-0065b`, `US-INFRA-0830`) — chủ của một khoảng im phải tra ngược được. */
export const GUIDE_TASK_REF_RE = /^US-[A-Z0-9]+(?:\.[A-Z0-9]+)?-\d{4}[a-z]?$/;
/** QĐ PO phải mang NGÀY đầy đủ — "PO bảo thế" không tra ngược được. */
export const GUIDE_PO_DECISION_RE = /QĐ PO \d{2}\/\d{2}\/\d{4}/;

/** Hình của `decision` theo loại. Trả lý do hỏng, `null` khi đúng hình. */
function decisionProblem(d: GuideSilentDecision | undefined): string | null {
  if (!d || typeof d !== "object") return "thiếu `decision`";
  if (d.kind === "po-decision") return GUIDE_PO_DECISION_RE.test(d.ref) ? null : "po-decision phải có 'QĐ PO dd/mm/yyyy'";
  if (d.kind === "owned-task" || d.kind === "structural") return GUIDE_TASK_REF_RE.test(d.ref) ? null : `${d.kind} phải trỏ một mã task`;
  return `loại quyết định lạ: ${String((d as { kind?: unknown }).kind)}`;
}

export function buildGuideMatrix(input: GuideMatrixInput = DEFAULT_INPUT): GuideMatrix {
  const problems: GuideMatrixProblem[] = [];
  const enumSet = new Set<string>(input.triggers);
  const silentTrig = new Map(input.silentTriggers.map((s) => [s.trigger, s]));
  const silentCell = new Map(input.silentCombos.map((s) => [`${s.trigger}|${roleKey(s.role)}`, s]));

  const reasonDetail = `lý do < ${GUIDE_SILENT_REASON_MIN} ký tự hoặc < ${GUIDE_SILENT_REASON_MIN_WORDS} từ có chữ`;
  for (const s of input.silentTriggers) {
    if (!enumSet.has(s.trigger)) {
      problems.push({ code: "SILENT_UNKNOWN_TRIGGER", trigger: s.trigger, detail: "khai im lặng cho trigger không còn trong enum" });
      continue;
    }
    if (!hasReason(s.reason)) problems.push({ code: "SILENT_WITHOUT_REASON", trigger: s.trigger, detail: `im cả hàng: ${reasonDetail}` });
    const bad = !GUIDE_TASK_REF_RE.test(String(s.owner)) ? `owner "${String(s.owner)}" không phải mã task` : decisionProblem(s.decision);
    if (bad) problems.push({ code: "SILENT_WITHOUT_OWNER", trigger: s.trigger, detail: `im cả hàng: ${bad}` });
  }
  for (const s of input.silentCombos) {
    if (!enumSet.has(s.trigger)) {
      problems.push({ code: "SILENT_UNKNOWN_TRIGGER", trigger: s.trigger, role: s.role, detail: "khai ô im cho trigger không còn trong enum" });
      continue;
    }
    if (!hasReason(s.reason)) problems.push({ code: "SILENT_WITHOUT_REASON", trigger: s.trigger, role: s.role, detail: `ô im: ${reasonDetail}` });
    const bad = decisionProblem(s.decision);
    if (bad) problems.push({ code: "SILENT_WITHOUT_OWNER", trigger: s.trigger, role: s.role, detail: `ô im: ${bad}` });
  }
  // Trần KHÍT: vượt = khoảng trống mới lọt vào không ai nới trần có tên; thừa = ratchet mất răng (hạ danh sách mà quên hạ trần).
  const caps = input.caps;
  if (caps) {
    for (const [list, cap, name] of [
      [input.silentTriggers, caps.triggers, "MAX_SILENT_TRIGGERS"],
      [input.silentCombos, caps.combos, "MAX_SILENT_COMBOS"],
    ] as const) {
      const first = list[0]?.trigger ?? (input.triggers[0] as GuideTrigger);
      if (list.length > cap) problems.push({ code: "SILENT_OVER_CAP", trigger: first, detail: `${list.length} dòng khai > trần ${name}=${cap}` });
      if (list.length < cap) problems.push({ code: "SILENT_CAP_STALE", trigger: first, detail: `${list.length} dòng khai < trần ${name}=${cap} — hạ trần cùng PR` });
    }
  }

  const rows = input.triggers.map((trigger): GuideMatrixRow => {
    const cards = input.library.filter((c) => c.triggers.includes(trigger)).sort(byBandThenPriority);
    const rowSilent = silentTrig.get(trigger);
    if (cards.length === 0 && !rowSilent) {
      problems.push({ code: "TRIGGER_WITHOUT_CARD", trigger, detail: "trigger có trong enum mà 0 thẻ và không khai im lặng" });
    }
    if (cards.length > 0 && rowSilent) {
      problems.push({ code: "SILENT_TRIGGER_HAS_CARD", trigger, detail: `khai im cả hàng nhưng đã có ${cards.length} thẻ — gỡ lời khai` });
    }
    const cells = GUIDE_MATRIX_ROLES.map((role): GuideMatrixCell => {
      const own = guideCardsFor(trigger, role, input.library);
      const combo = silentCell.get(`${trigger}|${roleKey(role)}`);
      if (own.length > 0) {
        if (combo) problems.push({ code: "SILENT_CELL_HAS_CARD", trigger, role, detail: "khai ô im nhưng ô đã có thẻ — gỡ lời khai" });
        return { kind: "cards", trigger, role, cards: own };
      }
      if (combo) return { kind: "silent", trigger, role, scope: "cell", reason: combo.reason };
      if (rowSilent) return { kind: "silent", trigger, role, scope: "trigger", reason: rowSilent.reason, owner: rowSilent.owner };
      // Hàng 0 thẻ không khai ⇒ đã có TRIGGER_WITHOUT_CARD cho cả hàng; không nhân thêm 5 vấn đề cấp ô cho cùng một bệnh.
      if (cards.length > 0) problems.push({ code: "CELL_WITHOUT_REASON", trigger, role, detail: "ô không thẻ nào và không khai lý do im" });
      return { kind: "empty", trigger, role };
    });
    return { trigger, cards, cells };
  });

  return { roles: GUIDE_MATRIX_ROLES, rows, problems };
}

/**
 * Lời giải TIẾNG VIỆT cho PO đọc ma trận. `satisfies Record<…>` ⇒ thêm trigger/tín hiệu/bậc mà quên lời giải là **TS đỏ**
 * (lỗi biên dịch > test đỏ > bảng in tên máy trần lên cho PO). Chỉ phục vụ bảng duyệt — engine không đọc.
 */
export const GUIDE_TRIGGER_GLOSS_VI = Object.freeze({
  OPEN: "Mở trợ lý",
  RETURN_AFTER_ABSENCE: "Quay lại sau thời gian vắng",
  TASK_DONE: "Vừa làm xong một việc",
  ASKED_NEXT: "Hỏi \"giờ làm gì tiếp\"",
  BLOCKED: "Bị chặn — việc chưa mở cho mình",
  BEFORE_RISKY_CONFIRM: "Sắp bấm Đồng ý một phiếu có rủi ro",
  TASK_FAILED: "Bấm Đồng ý mà việc không ghi được",
  STUCK: "Hỏi qua lại mãi chưa ra việc",
  DUTY_CREATED: "Người khác vừa giao cho mình một khoản phải đóng",
  MY_ITEM_RESOLVED: "Khoản mình báo đã được xác nhận/từ chối",
  MY_ROLE_CHANGED: "Vai của mình trong nhóm vừa đổi",
  DEADLINE_NEAR: "Sắp tới hạn đóng",
  STALE: "Có việc đang làm dở (phiếu chưa xong)",
  PERIOD_START: "Bắt đầu một kỳ mới",
  MILESTONE: "Nhóm đạt một cột mốc",
  CONFIG_CHANGED: "Cài đặt quan trọng của nhóm vừa đổi",
  ACCOUNT_RISK: "Tài khoản có dấu hiệu rủi ro",
} satisfies Record<GuideTrigger, string>);

export const GUIDE_SIGNAL_GLOSS_VI = Object.freeze({
  hasAnyGroup: "đã ở trong ít nhất một nhóm",
  groupCount: "số nhóm của bạn",
  fundCount: "số quỹ của nhóm",
  memberCount: "số thành viên của nhóm",
  openCollectionCount: "số đợt thu đang mở",
  myUnpaidDueCount: "số khoản bạn chưa đóng",
  myClaimedDueCount: "số khoản bạn đã báo chuyển, chờ đối chiếu",
  myClaimedAllFrozen: "MỌI khoản bạn đã báo chuyển (đang chờ đối chiếu) nằm ở quỹ đang tạm khoá (hoặc nhóm đình chỉ / lưu trữ)",
  pendingConfirmations: "số lời báo đã chuyển đang chờ bạn đối chiếu",
  role: "vai của bạn",
  groupName: "tên nhóm",
  turnFailed: "lượt vừa rồi ghi không được",
  turnsWithoutProgress: "số lượt liên tiếp chưa ra việc",
  unfinishedTaskCount: "số việc bạn đang làm dở ở nhóm này (phiếu chưa xong, mọi hội thoại)",
  unfinishedTaskMoney: "việc làm dở gần nhất là việc tiền (thu · chi · báo đã chuyển · ghi nhận · đóng hộ)",
  unfinishedOtherCount: "số việc làm dở KHÁC ngoài việc được mời làm tiếp",
  myRoleChanged: "vai của bạn vừa đổi",
  configChangedCount: "số thay đổi cài đặt quan trọng từ lần trước bạn ghé",
  askedGroupNoSeat: "bạn không có chỗ trong nhóm đang hỏi",
  myJoinPendingCount: "số nhóm bạn đang xin vào, chờ duyệt",
  pendingJoinRequests: "số lời xin vào nhóm đang chờ bạn duyệt",
  myInviteCount: "số nhóm đang mời bạn (lời mời chưa nhận, còn hạn)",
  myOpenDuesSettled: "mọi khoản của bạn ở đợt đang thu đã được xác nhận",
  openCollectionRemindable: "đợt đang thu còn người nhắc được (chưa đóng, chưa báo chuyển)",
  openCollectionRemindedRecently: "đợt đó vừa được nhắc TAY trong 3 ngày qua (không tính nhắc tự động)",
  bankAccountMissing: "nhóm chưa có số tài khoản nhận tiền",
  myReportAutoConfirm: "cách lời tự khai của bạn vào sổ (AUTO ngay · REVIEW chờ đối chiếu · MIXED tuỳ quỹ · NONE không còn khoản tự khai được)",
  configBankChanged: "trong các thay đổi có tài khoản nhận tiền của nhóm",
  configOtherChanged: "trong các thay đổi có cài đặt khác ngoài tài khoản nhận tiền",
  groupPendingInviteCount: "số lời mời qua email của nhóm đang chờ người nhận",
  stuckTurnKind: "lượt vừa rồi (UNCLEAR chưa hiểu ý · UNREAD câu dài chưa đọc hết · UNDERSTOOD hiểu nhưng không có việc)",
  myOpenVoteCount: "số khoản chi đang chờ bạn bỏ phiếu",
  myPayOnBehalfCount: "số lời đề nghị đóng hộ khoản của bạn đang chờ bạn trả lời",
  pendingExpenseApprovals: "số khoản chi (gồm yêu cầu chi của thành viên) đang chờ bạn duyệt thẳng được — không gồm khoản phải biểu quyết",
  myPayOnBehalfCreatesDebt: "quỹ của MỌI lời đề nghị đóng hộ đó đặt «đồng ý thì bạn nợ lại người đóng hộ» (cài đặt quỹ — trộn/không biết ⇒ không)",
} satisfies Record<GuideSignalKey, string>);

/** Câu PHỦ ĐỊNH cho tín hiệu có/không (vế `falsy`) — thiếu ở đây thì bảng rơi về "KHÔNG <lời giải>". */
export const GUIDE_SIGNAL_NEGATION_VI: Readonly<Partial<Record<GuideSignalKey, string>>> = Object.freeze({
  hasAnyGroup: "chưa ở nhóm nào",
  askedGroupNoSeat: "bạn có chỗ trong nhóm đang hỏi",
  turnFailed: "lượt vừa rồi ghi được",
  myRoleChanged: "vai của bạn không đổi",
  bankAccountMissing: "nhóm ĐÃ có số tài khoản nhận tiền",
  myClaimedAllFrozen: "có khoản bạn đã báo nằm ở quỹ không khoá",
  configOtherChanged: "chỉ có thay đổi tài khoản nhận tiền",
  unfinishedTaskMoney: "việc làm dở gần nhất là việc dựng nhóm (không phải việc tiền)",
});

export const GUIDE_BAND_GLOSS_VI = Object.freeze({
  BLOCKED: "Chặn đường",
  IN_FLIGHT: "Đang làm dở",
  DEADLINE: "Sát hạn",
  DUTY: "Nghĩa vụ tiền",
  SETUP: "Thiết lập",
  HYGIENE: "Gọn gàng",
  IDLE: "Thư thả",
} satisfies Record<GuideBand, string>);

export const GUIDE_CTALESS_GLOSS_VI = Object.freeze({
  waiting_on_others: "không nút — đang chờ người khác",
  informational: "không nút — chỉ báo tin",
  blocked: "không nút riêng — lượt đó tự mang lối ra (thử lại / chọn việc)",
} satisfies Record<NonNullable<GuideCard["ctaless"]>, string>);

/**
 * 🔴 US-AIV2-tourguide-0004 (@UC0360) — BẢNG BIẾN THỂ cho PO duyệt câu (`gen-guide-matrix.mjs` in): mỗi thẻ × MỌI biến thể theo đúng thứ tự
 * chọn (biến thể đầu tiên khớp thắng), kèm kết quả nút + điều kiện tín hiệu + có nút hay không. `BASE` = câu `templateVi` của thẻ, đứng
 * cuối nhóm `ready` (thẻ có nút) / cuối cùng (thẻ không nút). Một nguồn với `pickGuideVariant` — bảng in đúng thứ máy chọn.
 */
export interface GuideVariantRow {
  readonly cardId: string;
  readonly variantId: string;
  /** Kết quả nút chọn biến thể này (`[]` cho thẻ không nút). */
  readonly outcomes: readonly GuideCardOutcome[];
  readonly when: readonly GuidePredicate[];
  /** `null` = thẻ IM với lựa chọn này. */
  readonly templateVi: string | null;
  readonly button: boolean;
  readonly claims: readonly GuideClaim[];
  readonly note?: string;
}

export function guideVariantRows(card: GuideCard): GuideVariantRow[] {
  const possible = guideCardOutcomes(card);
  const hasButton = card.cta !== undefined || card.ctaFrom !== undefined;
  const rows: GuideVariantRow[] = (card.variants ?? []).map((v) => {
    const outcomes = (v.outcomes ?? []).filter((o) => possible.includes(o));
    return {
      cardId: card.id,
      variantId: v.id,
      outcomes,
      when: v.when ?? [],
      templateVi: v.silent === true ? null : (v.templateVi ?? null),
      button: outcomes.includes("ready"),
      claims: v.claims ?? [],
      ...(v.note === undefined ? {} : { note: v.note }),
    };
  });
  const base: GuideVariantRow = {
    cardId: card.id,
    variantId: `${card.id}/BASE`,
    outcomes: hasButton ? ["ready"] : [],
    when: [],
    templateVi: card.templateVi,
    button: hasButton,
    claims: card.claims ?? [],
  };
  // BASE đứng ngay sau biến thể `ready` cuối cùng (thứ tự chọn thật của `pickGuideVariant` cho `ready`).
  const lastReady = rows.map((r) => r.outcomes.includes("ready")).lastIndexOf(true);
  return !hasButton ? [...rows, base] : [...rows.slice(0, lastReady + 1), base, ...rows.slice(lastReady + 1)];
}
